import { HStack, Text, VStack, usePrevious } from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import {
  CreatableProps,
  CreatableSelect,
  FormatOptionLabelMeta,
  GroupBase,
  InputActionMeta,
  MultiValue,
  SelectInstance,
  SingleValue,
  useChakraSelectProps
} from "chakra-react-select"
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useHistory } from "react-router-dom"
import { AUGMENTATION_PANEL_AVATAR_CSS } from "../augmentation/augmentationPanelStyles"
import UserAvatar, { AvatarProps, UserAvatarProps } from "../avatar/UserAvatar"
import Link, { LinkTo } from "../link/Link"
import Pathicon from "../pathicon/Pathicon"
import ItemInput, {
  ItemInputImage,
  ItemInputProps,
  ItemInputValue
} from "./ItemInput"
import { CustomSelectProps, getSelectCustomComponents } from "./select"

interface FilterOptionOption<Option> {
  readonly label: string
  readonly value: string
  readonly data: Option
}

export type ItemSelectorOption = ItemInputValue & {
  id: number
}

export type ItemAvatarProps = Pick<UserAvatarProps, "user"> &
  Partial<Omit<UserAvatarProps, "user" | "children">>

const ITEM_SELECTOR_AVATAR_PROPS = {
  ...AUGMENTATION_PANEL_AVATAR_CSS,
  flexShrink: 0
} satisfies Omit<AvatarProps, "children">

export function ItemSelectorAvatar(props: ItemAvatarProps) {
  return <UserAvatar {...props} {...ITEM_SELECTOR_AVATAR_PROPS} />
}

const CREATE_OPTION_ID = -1

type SelectProps<T extends ItemSelectorOption> = CreatableProps<
  T,
  boolean,
  GroupBase<T>
> &
  CustomSelectProps

export type ItemSelectorProps<T extends ItemSelectorOption> = Omit<
  Partial<SelectProps<T>>,
  "getNewOptionData" | "isLoading" | "onChange" | "options" | "value"
> & {
  options?: T[]
  value?: T | null
  onChange: (option?: T | null) => void
  itemLabel: string
  isLoading: boolean
  mode?: "create" | "select" | "create-or-select"
  icon?: IconName
  getNewOptionData: Required<
    CreatableProps<T, boolean, GroupBase<T>>
  >["getNewOptionData"]
  getItemImage?: (option: T) => string | null | undefined
  getItemAvatarProps?: (option: T) => ItemAvatarProps | undefined
  onInputValueChange?: (inputValue: string) => void
  renderItemContent?: (
    option: T,
    context: FormatOptionLabelMeta<T>["context"]
  ) => ReactNode
  renderInputLeftElement?: (options: {
    value?: T | null
    inputValue: string
    icon?: IconName
  }) => ReactNode
  itemInputProps?: Omit<
    ItemInputProps<T>,
    "icon" | "leftElement" | "onChange" | "value"
  >
  /** When set, the create menu row navigates instead of opening inline create input. */
  getNavigateCreateTo?: (inputValue: string) => LinkTo

  // When false, prevents the default auto-focus behavior when the value is empty.
  focusOnEmptyValue?: boolean
  /** Disable for remote searches where a single result must still be chosen explicitly. */
  autoSelectSingleOption?: boolean
}

function isCreateOption<T extends ItemSelectorOption>(
  option: T | null | undefined
) {
  return option?.id === CREATE_OPTION_ID
}

function useCustomChakraSelectProps<
  T extends ItemSelectorOption
>(): SelectProps<T> {
  return useChakraSelectProps({
    chakraStyles: {
      container: (provided) => ({
        ...provided,
        w: "100%"
      }),
      option: (provided, state) => ({
        ...provided,
        color: "gray.900",
        w: "initial",
        bg:
          state.isFocused && state.isSelected
            ? "gray.300"
            : state.isFocused
            ? "gray.200"
            : state.isSelected
            ? "gray.100"
            : "transparent"
      }),
      menu: (provided) => ({
        ...provided,
        borderRadius: "md"
      }),
      menuList: (provided) => ({
        ...provided,
        padding: 0,
        border: "none",
        borderRadius: "md"
      }),
      placeholder: (provided) => ({
        ...provided,
        paddingLeft: "2px",
        fontSize: "inherit",
        lineClamp: 1
      }),
      input: (provided) => ({
        ...provided,
        fontSize: "inherit"
      }),
      control: (provided, state) => ({
        ...provided,
        padding: "0 .8em",
        border: "1px solid",
        borderColor: "gray.100",
        color: "gray.900",
        borderRadius: "20px",
        background: state.isFocused
          ? "whiteAlpha.200 !important"
          : "transparent",
        _hover: {
          background: "whiteAlpha.100"
        }
      }),
      clearIndicator: (provided) => ({
        ...provided,
        display: "none"
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        mr: `calc(var(--input-height) - var(--tipped-field-icon-width))`
      }),
      valueContainer: (provided) => ({
        ...provided,
        pl: 0
      })
    }
  })
}

function getItemImage<T extends ItemSelectorOption>(
  option: T | null | undefined,
  getImage?: (option: T) => string | null | undefined
) {
  if (!option || !getImage) return null
  const image = getImage(option)
  return typeof image === "string" && !!image.trim() ? image : null
}

function renderItemLeadingVisual<T extends ItemSelectorOption>(
  option: T,
  {
    icon,
    getItemImage: getItemImageFn,
    getItemAvatarProps,
    showIconFallback = true
  }: {
    icon?: IconName
    getItemImage?: (option: T) => string | null | undefined
    getItemAvatarProps?: (option: T) => ItemAvatarProps | undefined
    showIconFallback?: boolean
  }
) {
  const itemAvatarProps = getItemAvatarProps?.(option)

  if (itemAvatarProps) {
    return <ItemSelectorAvatar {...itemAvatarProps} />
  }

  const image = getItemImage(option, getItemImageFn)

  if (image) {
    return <ItemInputImage image={image} />
  }

  if (showIconFallback && icon) {
    return <Pathicon icon={icon} />
  }

  return null
}

function defaultRenderItemContent<T extends ItemSelectorOption>(
  option: T,
  context: FormatOptionLabelMeta<T>["context"],
  {
    icon,
    getItemImage,
    getItemAvatarProps
  }: {
    icon?: IconName
    getItemImage?: (option: T) => string | null | undefined
    getItemAvatarProps?: (option: T) => ItemAvatarProps | undefined
  }
) {
  const showIconFallback = context === "menu"
  const leadingVisual = renderItemLeadingVisual(option, {
    icon,
    getItemImage,
    getItemAvatarProps,
    showIconFallback
  })

  if (context === "menu") {
    return (
      <VStack gap={0} w="100%">
        <HStack w="100%" justifyContent="flex-start">
          {leadingVisual}
          <Text as="span" mr="auto">
            {option.name}
          </Text>
        </HStack>
      </VStack>
    )
  }

  // Selected value: only show an image/avatar when available. The control
  // already renders `icon` on the left — do not duplicate it here.
  return (
    <HStack>
      {leadingVisual}
      <Text className="ItemSelector__name" as="span" color="whiteAlpha.900">
        {option.name}
      </Text>
    </HStack>
  )
}

function ItemSelector<T extends ItemSelectorOption>({
  options,
  value,
  onChange,
  isLoading,
  itemLabel,
  getNewOptionData,
  mode = "create-or-select",
  icon,
  getItemImage: getItemImageProp,
  getItemAvatarProps,
  onInputValueChange,
  renderItemContent,
  renderInputLeftElement,
  itemInputProps,
  chakraStyles: customChakraStyles,
  renderControlLeftElement: renderControlLeftElementProp,
  renderControlRightElement,
  getNavigateCreateTo,
  focusOnEmptyValue = true,
  autoSelectSingleOption = true,
  ...selectProps
}: ItemSelectorProps<T>) {
  const history = useHistory()
  const selectRef = useRef<SelectInstance<T, boolean, GroupBase<T>>>(null)
  const [inputValue, setInputValue] = useState("")
  const [explicitlyCreatingOption, setExplicitlyCreatingOption] =
    useState(false)

  const createItemName = `Create ${itemLabel.toLowerCase()}...`
  const usesNavigateCreate = Boolean(getNavigateCreateTo)

  const resolvedMode =
    !options?.length && !isLoading && !usesNavigateCreate ? "create" : mode

  const shouldAutoSelectSingleOption =
    autoSelectSingleOption &&
    !isLoading &&
    options?.length === 1 &&
    resolvedMode === "select"

  useEffect(() => {
    if (shouldAutoSelectSingleOption) {
      handleChange(options[0])
    }
  }, [shouldAutoSelectSingleOption])

  const resetAfterNavigateCreate = useCallback(() => {
    setInputValue("")
    onInputValueChange?.("")
    selectRef.current?.blur()
  }, [onInputValueChange])

  const handleNavigateCreate = useCallback(() => {
    if (!getNavigateCreateTo) return

    const to = getNavigateCreateTo(inputValue)

    history.push(typeof to === "function" ? to(history.location) : to)
    resetAfterNavigateCreate()
  }, [getNavigateCreateTo, history, inputValue, resetAfterNavigateCreate])

  function handleChange(selected: SingleValue<T> | MultiValue<T>) {
    let item = (selected || null) as SingleValue<T> | null

    if (usesNavigateCreate && isCreateOption(item)) {
      handleNavigateCreate()
      return
    }

    if (item?.name === createItemName) {
      item = getNewOptionData(inputValue, null)
      setExplicitlyCreatingOption(true)
    }

    onChange(item)
    setInputValue("")
  }

  useEffect(() => {
    if (!focusOnEmptyValue) return

    if (!value && !selectProps.isDisabled) {
      selectRef.current?.focus()
      selectRef.current?.onMenuOpen()
    }
  }, [focusOnEmptyValue, selectProps.isDisabled, value])

  function formatOptionLabel(option: T, meta: FormatOptionLabelMeta<T>) {
    const createLabel = inputValue
      ? `Create "${inputValue}"`
      : `Create ${itemLabel.toLowerCase()}...`

    if (meta.context === "menu" && option.id === CREATE_OPTION_ID) {
      const createContent = (
        <HStack w="100%" justifyContent="flex-start">
          {icon ? <Pathicon icon={icon} /> : null}
          <Text as="span">{createLabel}</Text>
        </HStack>
      )

      if (usesNavigateCreate && getNavigateCreateTo) {
        return (
          <Link
            to={getNavigateCreateTo(inputValue)}
            w="100%"
            variant="plain"
            color="inherit"
            _hover={{ textDecor: "none" }}
            onMouseDown={(event) => {
              event.preventDefault()
              event.stopPropagation()
            }}
            onClick={(event) => {
              event.stopPropagation()
              resetAfterNavigateCreate()
            }}
          >
            {createContent}
          </Link>
        )
      }

      return createContent
    }

    if (renderItemContent) {
      return renderItemContent(option, meta.context)
    }

    return defaultRenderItemContent(option, meta.context, {
      icon,
      getItemImage: getItemImageProp,
      getItemAvatarProps
    })
  }

  function filterOptionName(option: T, currentInputValue: string) {
    return (
      option.name.toLowerCase().indexOf(currentInputValue.toLowerCase()) > -1
    )
  }

  function filterOption(
    option: FilterOptionOption<T>,
    currentInputValue: string
  ) {
    if (usesNavigateCreate && option.data.id === CREATE_OPTION_ID) {
      return true
    }

    return filterOptionName(option.data, currentInputValue)
  }

  function handleInputChange(
    currentInputValue: string,
    action: InputActionMeta
  ) {
    if (action.action !== "input-blur") {
      setInputValue(currentInputValue)
    }
    onInputValueChange?.(currentInputValue)
  }

  const chakraSelectProps = useCustomChakraSelectProps<T>()
  const mergedChakraStyles = useMemo(
    () => ({
      ...chakraSelectProps.chakraStyles,
      ...customChakraStyles
    }),
    [chakraSelectProps.chakraStyles, customChakraStyles]
  )
  const selectComponents = useMemo(
    () => getSelectCustomComponents<T, boolean, CustomSelectProps>(),
    []
  )
  const selectedItemAvatarProps =
    value && getItemAvatarProps ? getItemAvatarProps(value) : undefined
  const selectedItemImage =
    !selectedItemAvatarProps && getItemImage(value, getItemImageProp)
  const selectedItemHasVisual = Boolean(
    selectedItemAvatarProps || selectedItemImage
  )

  const renderControlLeftElement = useMemo(
    () => () => {
      const customLeftElement = renderControlLeftElementProp?.()

      if (customLeftElement !== undefined) {
        return customLeftElement
      }

      // A selected value with an avatar or image renders its own leading
      // visual, so the control's icon would duplicate it.
      return selectedItemHasVisual ? null : undefined
    },
    [renderControlLeftElementProp, selectedItemHasVisual]
  )

  useEffect(() => {
    if (!inputValue) {
      setExplicitlyCreatingOption(false)
    }
  }, [inputValue])

  const isCreatingNewOption = useMemo(() => {
    if (usesNavigateCreate) return false
    if (!options) return true
    if (explicitlyCreatingOption) return true

    if (
      !!inputValue &&
      !options.filter((option) => filterOptionName(option, inputValue)).length
    ) {
      return true
    }

    return false
  }, [options, explicitlyCreatingOption, inputValue, usesNavigateCreate])

  const prevInputValue = usePrevious(inputValue)
  useEffect(() => {
    if (!inputValue) {
      if (
        inputValue !== prevInputValue &&
        value?.id === -1 &&
        resolvedMode !== "create"
      ) {
        onChange()
      }
    } else if (isCreatingNewOption && resolvedMode !== "select") {
      const data =
        !value || value.id !== -1 ? getNewOptionData(inputValue, null) : value

      if (data) {
        onChange({
          ...data,
          name: inputValue
        })
      }
    }
  }, [isCreatingNewOption, inputValue])

  const inputIcon = selectedItemHasVisual && !inputValue ? undefined : icon
  const defaultInputLeftElement = selectedItemAvatarProps ? (
    <ItemSelectorAvatar {...selectedItemAvatarProps} />
  ) : selectedItemImage ? (
    <ItemInputImage image={selectedItemImage} />
  ) : null

  // Stable reference so react-select does not close the menu on each keystroke.
  const createMenuOption = useMemo(
    () => getNewOptionData(createItemName, null),
    [createItemName, getNewOptionData]
  )

  const availableOptions = useMemo(() => {
    const optionsWithCreateOption = [createMenuOption, ...(options || [])]

    if (usesNavigateCreate) {
      return optionsWithCreateOption
    }

    return inputValue || resolvedMode === "select"
      ? options
      : optionsWithCreateOption
  }, [createMenuOption, inputValue, options, resolvedMode, usesNavigateCreate])

  const controlRightElement = renderControlRightElement?.()
  const rendersCreateInput =
    (isCreatingNewOption && resolvedMode !== "select") ||
    resolvedMode === "create"

  if (rendersCreateInput) {
    const createInput = (
      <ItemInput
        value={value!}
        onChange={(nextValue) => {
          onChange(nextValue as T)
          setInputValue(nextValue.name)
        }}
        onKeyDown={(e) => {
          itemInputProps?.onKeyDown?.(e)

          if (e.defaultPrevented) return

          if (e.key === "Backspace" && e.currentTarget.value === "") {
            onChange()
            setExplicitlyCreatingOption(false)
          }
        }}
        placeholder={
          (selectProps.placeholder as string) || `Create a ${itemLabel}`
        }
        disabled={selectProps.isDisabled || selectProps.disabled}
        icon={inputIcon}
        leftElement={
          renderInputLeftElement?.({
            value,
            inputValue,
            icon: inputIcon
          }) ?? defaultInputLeftElement
        }
        {...itemInputProps}
      />
    )

    // The create input has no select control to host a trailing element, so it
    // sits alongside the input instead.
    return controlRightElement ? (
      <HStack gap="5px" w="100%">
        {createInput}
        {controlRightElement}
      </HStack>
    ) : (
      createInput
    )
  }

  return (
    <CreatableSelect
      className="ItemSelector--CreatableSelect"
      ref={selectRef}
      options={availableOptions}
      value={value}
      inputValue={inputValue}
      getOptionValue={(option) => option.id + ""}
      onChange={handleChange}
      onInputChange={handleInputChange}
      formatOptionLabel={formatOptionLabel}
      filterOption={filterOption}
      placeholder={
        resolvedMode === "create-or-select" || usesNavigateCreate
          ? `Select or create a ${itemLabel}`
          : `Select a ${itemLabel}`
      }
      aria-label={`Select ${itemLabel}`}
      variant="subtle"
      isClearable={true}
      escapeClearsValue
      hideSelectedOptions={false}
      isSearchable={true}
      // Auto-selection resolves the empty value without user input, so don't
      // steal focus in that case. If auto-selection is disabled (e.g. remote
      // search), keep normal focus behavior so the user can choose the result.
      autoFocus={
        focusOnEmptyValue &&
        (!value || value.name === inputValue) &&
        !shouldAutoSelectSingleOption
      }
      // Likewise, don't flash a menu for a result we're already selecting.
      // A lone result still needs an open menu when explicit selection is required.
      openMenuOnFocus={!value && !shouldAutoSelectSingleOption}
      closeMenuOnSelect={true}
      backspaceRemovesValue={true}
      isLoading={isLoading}
      loadingMessage={() => null}
      noOptionsMessage={() => null}
      createOptionPosition="first"
      getNewOptionData={getNewOptionData}
      isValidNewOption={usesNavigateCreate ? () => false : undefined}
      onCreateOption={(nextValue) => {
        const item = getNewOptionData(nextValue, null)
        onChange(item)
        setInputValue(item.name)
        setExplicitlyCreatingOption(true)
      }}
      icon={icon}
      {...{
        ...({
          ...chakraSelectProps,
          chakraStyles: mergedChakraStyles,
          components: {
            ...chakraSelectProps.components,
            ...selectComponents
          },
          renderControlLeftElement,
          renderControlRightElement
        } as SelectProps<T>)
      }}
      {...selectProps}
    />
  )
}

export default ItemSelector
